import WandbLoader, {
  WandbLoaderProps,
} from '@wandb/weave/common/components/WandbLoader';
import {
  ProfileData,
  useLifecycleProfiling,
} from '@wandb/weave/common/hooks/useLifecycleProfiling';
// @todo: Migrate to 'PotentialRoadmap' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {
  WBLogoLoader,
  WBLogoLoaderProps,
} from '@wandb/weave/components/Loaders/WBLogoLoader';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router';
// this will disappear when the loaders are migrated to the new loader
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Loader as SemanticLoaderBase,
  StrictLoaderProps,
} from 'semantic-ui-react';

import {getDatadogRum} from '../../util/datadog/datadogRum';
import {useDurationVital} from '../../util/datadog/useDurationVital';
import {envIsDev} from './../../config';
import {useSelector} from './../../state/hooks';

declare global {
  interface Window {
    WANDB_LOG_LOADERS?: boolean;
  }
}

/**
 * Allow an override which lets us log the name of a loader. It's written this way so that we can selectively toggle it in prod since certain loaders that show in prod don't show in dev, and they're exceedingly hard to go track down
 */
const shouldLogLoaders = () => window?.WANDB_LOG_LOADERS;

const useDevDebugging = (
  name: string,
  loaderType: 'wave' | 'wb-logo' | 'spinner'
) => {
  useEffect(() => {
    const mount = performance.now();

    if (shouldLogLoaders()) {
      console.log(`${name} mounted at ${mount}`);
    }
    return () => {
      if (shouldLogLoaders()) {
        const duration = performance.now() - mount;
        console.log(`${name} (${loaderType}) unmounted in ${duration}ms`);
      }
    };
  }, [name, loaderType]);

  return null;
};

export const SemanticLoader = (props: StrictLoaderProps & {name: string}) => {
  useDevDebugging(props.name, 'spinner');

  return <SemanticLoaderBase {...props} />;
};

/**
 * Sets up instrumentation for the loader. It adds a datadog vital measurement
 * that measures the time between mounting and unmounting.
 *
 * Uses useLifecycleProfiling hook to track component lifecycle and datadog to record the duration.
 * Sends custom actions to Datadog with additional context for all loader completions.
 *
 * @param name - The name of the loader for tracking purposes
 */
function useInstrumentedLoaderFns(name: string) {
  const startTime = useRef(Date.now());
  const {record} = useDurationVital(`loaders__${name}`);
  const datadogRum = getDatadogRum();
  const location = useLocation();
  const viewerData = useSelector(state => state.viewer.viewer);

  const handleComplete = useCallback(
    (data: ProfileData) => {
      // Always record the duration vital (100% of loaders)
      record(startTime.current, data.duration);

      // Custom action with additional context
      datadogRum.addAction('loader-completed', {
        loaderName: name,
        componentId: data.id,
        duration: data.duration,
        entity: viewerData?.entity || null,
        location: location.pathname,
        organizations: viewerData?.organizations.map(org => org.id) || [],
        user: viewerData?.id || null,
      });
    },
    [record, datadogRum, name, location.pathname, viewerData]
  );

  useLifecycleProfiling(name, handleComplete);
}

export const InstrumentedWaveLoader = (props: {
  name: string;
  size: 'small' | 'huge';
  delayBeforeShow?: number;
}) => {
  useInstrumentedLoaderFns(props.name);
  useDevDebugging(props.name, 'wave');

  return (
    <WaveLoader size={props.size} delayBeforeShow={props.delayBeforeShow} />
  );
};

export const InstrumentedCenteredWaveLoader = (props: {
  name: string;
  size: 'small' | 'huge';
}) => {
  return (
    <TailwindContents>
      <div className="flex h-full w-full items-center justify-center">
        <InstrumentedWaveLoader {...props} size="huge" />
      </div>
    </TailwindContents>
  );
};

export const InstrumentedFloatingWaveLoader = (props: {
  name: string;
  size: 'small' | 'huge';
}) => {
  return (
    <TailwindContents>
      <div className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center">
        <InstrumentedWaveLoader {...props} size="huge" />
      </div>
    </TailwindContents>
  );
};

export const InstrumentedWBLogoLoader = (
  props: {name: string} & WBLogoLoaderProps
) => {
  useInstrumentedLoaderFns(props.name);
  useDevDebugging(props.name, 'wb-logo');

  return <WBLogoLoader {...props} />;
};

export const InstrumentedLoader = (
  props: {name: string} & WandbLoaderProps
) => {
  useInstrumentedLoaderFns(props.name);
  useDevDebugging(props.name, 'spinner');

  return <WandbLoader {...props} />;
};
